import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Edit, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { AdminUser } from '@/hooks/useAdminUsers';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface UsersTableProps {
  users: AdminUser[];
  onEdit: (user: AdminUser) => void;
  onDelete: (userId: string) => Promise<boolean>;
  onCreate: () => void;
  loading: boolean;
}

type SortField = 'nom' | 'email' | 'role' | 'plan' | 'date_debut' | 'created_at' | 'last_sign_in_at' | 'bateaux';
type SortDirection = 'asc' | 'desc';

export const UsersTable = ({ users, onEdit, onDelete, onCreate, loading }: UsersTableProps) => {
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const computeSubscriptionType = (u: AdminUser) => {
    const plan = u.abonnements?.[0]?.plan;
    if (!plan || plan === 'gratuit') return 'gratuit' as const;
    const start = u.abonnements?.[0]?.date_debut ? new Date(u.abonnements[0].date_debut) : null;
    const end = u.abonnements?.[0]?.date_fin ? new Date(u.abonnements[0].date_fin!) : null;
    if (start && end) {
      const diffDays = Math.abs(+end - +start) / (1000 * 60 * 60 * 24);
      return diffDays > 330 ? 'annuel' : 'mensuel';
    }
    return 'mensuel' as const;
  };

  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'nom':
          const nameA = `${a.prenom || ''} ${a.nom || ''}`.trim().toLowerCase();
          const nameB = `${b.prenom || ''} ${b.nom || ''}`.trim().toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'email':
          comparison = (a.email || '').localeCompare(b.email || '');
          break;
        case 'role':
          comparison = (a.role || '').localeCompare(b.role || '');
          break;
        case 'plan':
          const planOrder = { 'annuel': 3, 'mensuel': 2, 'gratuit': 1 };
          const planA = planOrder[computeSubscriptionType(a)] || 0;
          const planB = planOrder[computeSubscriptionType(b)] || 0;
          comparison = planA - planB;
          break;
        case 'date_debut':
          const dateDebutA = a.abonnements?.[0]?.date_debut ? new Date(a.abonnements[0].date_debut).getTime() : 0;
          const dateDebutB = b.abonnements?.[0]?.date_debut ? new Date(b.abonnements[0].date_debut).getTime() : 0;
          comparison = dateDebutA - dateDebutB;
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'last_sign_in_at':
          const lastA = a.last_sign_in_at ? new Date(a.last_sign_in_at).getTime() : 0;
          const lastB = b.last_sign_in_at ? new Date(b.last_sign_in_at).getTime() : 0;
          comparison = lastA - lastB;
          break;
        case 'bateaux':
          comparison = (a.bateaux?.length || 0) - (b.bateaux?.length || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [users, sortField, sortDirection]);

  const handleDelete = async () => {
    if (!deleteUserId) return;
    
    setIsDeleting(true);
    try {
      const success = await onDelete(deleteUserId);
      if (success) {
        setDeleteUserId(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin':
        return 'destructive';
      case 'pro':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getPlanBadgeClasses = (type: 'gratuit' | 'mensuel' | 'annuel') => {
    switch (type) {
      case 'gratuit':
        return 'bg-background text-foreground border';
      case 'mensuel':
        return 'bg-success text-primary-foreground';
      case 'annuel':
        return 'bg-nautic-gold text-primary-foreground';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">Chargement des utilisateurs...</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-nautic">Gestion des Utilisateurs</h1>
          <Button onClick={onCreate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Créer un utilisateur
          </Button>
        </div>

        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('nom')}
                >
                  <span className="flex items-center">
                    Nom
                    {getSortIcon('nom')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('email')}
                >
                  <span className="flex items-center">
                    Email
                    {getSortIcon('email')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('role')}
                >
                  <span className="flex items-center">
                    Rôle
                    {getSortIcon('role')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('plan')}
                >
                  <span className="flex items-center">
                    Plan
                    {getSortIcon('plan')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('date_debut')}
                >
                  <span className="flex items-center">
                    Abonnement
                    {getSortIcon('date_debut')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  <span className="flex items-center">
                    Date d'inscription
                    {getSortIcon('created_at')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('last_sign_in_at')}
                >
                  <span className="flex items-center">
                    Dernière connexion
                    {getSortIcon('last_sign_in_at')}
                  </span>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none"
                  onClick={() => handleSort('bateaux')}
                >
                  <span className="flex items-center">
                    Bateaux
                    {getSortIcon('bateaux')}
                  </span>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">
                    {user.prenom || user.nom ? `${user.prenom || ''} ${user.nom || ''}`.trim() : '-'}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={getRoleBadgeVariant(user.role)}>
                      {user.role === 'admin' ? 'Admin' : 'Utilisateur'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const type = computeSubscriptionType(user);
                      const label = type;
                      return (
                        <Badge className={getPlanBadgeClasses(type)}>
                          {label}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">
                        Début: {user.abonnements?.[0]?.date_debut 
                          ? format(new Date(user.abonnements[0].date_debut), 'dd/MM/yyyy', { locale: fr })
                          : 'N/A'
                        }
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Fin: {user.abonnements?.[0]?.date_fin
                          ? format(new Date(user.abonnements[0].date_fin), 'dd/MM/yyyy', { locale: fr })
                          : 'N/A'
                        }
                      </div>
                      <Badge variant={user.abonnements?.[0]?.statut === 'actif' ? 'default' : 'secondary'} className="text-xs">
                        {user.abonnements?.[0]?.statut || 'actif'}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {format(new Date(user.created_at), 'dd/MM/yyyy', { locale: fr })}
                  </TableCell>
                  <TableCell>
                    {user.last_sign_in_at 
                      ? format(new Date(user.last_sign_in_at), 'dd/MM/yyyy HH:mm', { locale: fr })
                      : 'Jamais'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="font-medium">{user.bateaux?.length || 0} bateau(x)</div>
                      {user.bateaux && user.bateaux.length > 0 && (
                        <div className="text-sm text-muted-foreground">
                          {user.bateaux.slice(0, 2).map(bateau => bateau.nom).join(', ')}
                          {user.bateaux.length > 2 && ` +${user.bateaux.length - 2} autres`}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(user)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteUserId(user.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {users.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible
              et supprimera toutes les données associées (bateaux, inventaire, maintenances, etc.).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};